import React from "react";
import { useTranslation } from "react-i18next";
import { useIsDevOrSuperAdmin } from "../../../utils/useIsDevOrSuperAdmin";

function CalcNode({ calc, depth = 0 }) {
  const items = Array.isArray(calc?.items) ? calc.items : [];
  const nested = Array.isArray(calc?.nested_calculations)
    ? calc.nested_calculations
    : [];

  return (
    <li className="rounded border border-slate-200 bg-white">
      <div
        className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-3 py-2"
        style={{ paddingLeft: 12 + depth * 12 }}
      >
        <strong className="text-sm text-slate-900">
          {calc?.title || calc?.id || "—"}
        </strong>
        {calc?.total_amount != null ? (
          <span className="text-xs tabular-nums text-slate-600">
            {calc.total_amount}
          </span>
        ) : null}
      </div>
      {items.length ? (
        <ul className="divide-y divide-slate-100 px-3 py-1 text-xs text-slate-600">
          {items.map((item, index) => (
            <li
              key={item.id || `${calc.id}-item-${index}`}
              className="flex justify-between gap-2 py-1"
              style={{ paddingLeft: depth * 8 }}
            >
              <span>{item.description || "—"}</span>
              <span className="tabular-nums">
                {item.quantity || 1} × {item.sales_price || 0}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {nested.length ? (
        <ul className="space-y-2 border-t border-slate-100 p-2">
          {nested.map((child) => (
            <CalcNode key={child.id || child.title} calc={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * REV-001 — read-only view of locked snapshot_data.calculations.
 */
export default function OfferLockedCalculationTree({ calculations, caption }) {
  const { t } = useTranslation();
  const showDevHints = useIsDevOrSuperAdmin();
  const tree = Array.isArray(calculations) ? calculations : [];
  if (!tree.length) {
    return (
      <p className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-500 ring-1 ring-slate-200">
        {showDevHints
          ? t("offers.diff.no_locked_calc")
          : t("offers.diff.no_locked_calc_public")}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {caption ? (
        <h4 className="text-sm font-semibold text-slate-800">{caption}</h4>
      ) : (
        <h4 className="text-sm font-semibold text-slate-800">
          {t("offers.diff.locked_calc_title")}
        </h4>
      )}
      <p className="text-xs text-slate-500">{t("offers.diff.locked_calc_hint")}</p>
      <ul className="space-y-2">
        {tree.map((calc) => (
          <CalcNode key={calc.id || calc.title} calc={calc} />
        ))}
      </ul>
    </div>
  );
}
